import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, CheckCircle, ArrowRight, ArrowUpRight, Phone, Mail,
  Stethoscope, Cpu, Wrench, Truck, Sprout, ChevronDown,
  MapPin, GraduationCap, FileCheck, Sparkles, Building2, Wine, Mountain,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

/**
 * /countries/australia
 * ────────────────────────────────────────────────────────────────────────
 * Parent hub page consolidating the four Australian skilled-migration
 * pathways — Subclass 189 (Skilled Independent), 190 (State Nominated),
 * 482/186 (Employer-Sponsored / SID), 491/494 (Skilled Regional).
 * Source of truth: /app/memory/AUSTRALIA_HUB_STRATEGY_2026.md
 * ~3,000 words rendered. 1 H1 · 10+ H2 · 22+ H3 · 12 FAQs · 3 JSON-LD blocks.
 * Zero Canadian terminology (no CRS, no Express Entry, no PNP, no ECA, no IRCC, no CLB).
 */

const PRIMARY_PHONE = '+91-99588-48998';
const PRIMARY_EMAIL = 'info@zafco.in';
const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const PR_VISA_URL = '/study-abroad/australia/';
const SC_189_URL = '/skilled/australia/australia-skilled-independent-visa-189';
const SC_190_URL = '/skilled/australia/australia-skilled-nominated-visa-190';
const SC_482_URL = '/skilled/australia/australia-subclass-482-visa';
const SC_491_URL = '/skilled/australia/nominated-or-sponsored-visa-489';
const POINTS_CALC_URL = '/australia-pr-calculator';

const COMPARISON_ROWS = [
  {
    feature: 'Best for',
    sc189: 'High-points candidates in CSOL occupations who want a no-sponsor permanent visa',
    sc190: 'Candidates whose occupation is in demand in a specific state or territory',
    sc482: 'Candidates with a genuine Australian employer job offer',
    sc491: 'Candidates open to settling in regional Australia with a 3–5 year PR pathway',
  },
  {
    feature: 'Selection model',
    sc189: 'SkillSelect EOI ranked by points; invited via federal SkillSelect rounds',
    sc190: 'EOI + state/territory nomination; invited by the nominating state',
    sc482: 'Employer files nomination; visa application follows',
    sc491: 'EOI + state nomination or eligible regional employer sponsorship',
  },
  {
    feature: 'Points required',
    sc189: '65 minimum; invited cut-offs 75–95 in 2026',
    sc190: '65 + +5 state nomination bonus',
    sc482: 'Not points-based — role and salary based',
    sc491: '65 + +15 regional bonus + state nomination',
  },
  {
    feature: 'Occupation list',
    sc189: 'CSOL',
    sc190: 'CSOL + state-specific in-demand list',
    sc482: 'SID streams (Specialist / Core / Essential)',
    sc491: 'CSOL + regional state lists',
  },
  {
    feature: 'Skills assessment',
    sc189: 'Mandatory (VETASSESS / EA / ACS / ANMAC / TRA depending on occupation)',
    sc190: 'Mandatory',
    sc482: 'Mandatory for nominated occupation',
    sc491: 'Mandatory',
  },
  {
    feature: 'English requirement',
    sc189: 'Competent (IELTS 6.0 each band)',
    sc190: 'Competent (IELTS 6.0 each band)',
    sc482: 'Vocational (IELTS 5.0 each) for most streams',
    sc491: 'Competent (IELTS 6.0 each band)',
  },
  {
    feature: 'Sponsorship / nomination',
    sc189: 'Not required',
    sc190: 'State or territory nomination required',
    sc482: 'Approved Australian employer required',
    sc491: 'State nomination or approved regional employer required',
  },
  {
    feature: 'Path to PR',
    sc189: 'Permanent on grant',
    sc190: 'Permanent on grant',
    sc482: '186 ENS permanent (after qualifying 482 period)',
    sc491: '5-yr provisional → Subclass 191 PR after 3 yrs regional residence + tax history',
  },
];

const DECISION_ROWS = [
  ['189 points score 85+, CSOL occupation, no state preference', 'Subclass 189 — Skilled Independent', 'Start 189 EOI assessment', SC_189_URL],
  ['189 points 70–84, occupation on a state in-demand list', 'Subclass 190 — State Nomination', 'Start 190 nomination plan', SC_190_URL],
  ['Healthcare professional (nurse, allied health, GP)', 'Subclass 190 (VIC / SA / TAS) or 491 regional', 'Speak to a healthcare-stream counsellor', '/contact?source=australia-hub-healthcare'],
  ['Tradesperson (welder, electrician, plumber, HVAC, heavy-equipment)', 'Subclass 491 regional or 482 Essential Skills SID', 'Start trades-stream assessment', SC_491_URL],
  ['You already have an Australian employer offer', 'Subclass 482 / 186 (ENS)', 'Open employer-sponsored file', SC_482_URL],
  ['Points score 65–75, open to regional settlement', 'Subclass 491 / 494', 'Start regional-pathway plan', SC_491_URL],
];

const PATHWAY_BLOCKS = [
  {
    icon: Award,
    title: 'Subclass 189 — Skilled Independent',
    body: 'A points-tested, permanent skilled visa for applicants with a Core Skills Occupation List (CSOL) occupation, a positive skills assessment from the relevant assessing authority, Competent English (IELTS 6.0 in each band) and a competitive total points score. No employer, state or family sponsorship is required. Candidates file a SkillSelect Expression of Interest (EOI) and wait for an Invitation to Apply (ITA) in monthly federal rounds. In 2026, invited cut-offs have generally been 75 in healthcare, 80–85 in engineering / IT, and 90+ in accounting and ICT business analysts.',
    ctaLabel: 'Read the full Subclass 189 guide',
    ctaUrl: SC_189_URL,
  },
  {
    icon: MapPin,
    title: 'Subclass 190 — Skilled Nominated',
    body: 'A points-tested, permanent skilled visa nominated by a state or territory government. Applicants must hold a CSOL occupation that also appears on the nominating state\u2019s in-demand list, lodge a SkillSelect EOI tagged to that state, and meet the state\u2019s specific conditions (work-experience, residence, commitment to live in-state). Nomination adds +5 points and prioritises invitations for that occupation in that state. Indian applicants frequently use 190 to bridge the points gap when their 189 score sits in the 70–75 range.',
    ctaLabel: 'Read the full Subclass 190 guide',
    ctaUrl: SC_190_URL,
  },
  {
    icon: Building2,
    title: 'Subclass 482 (Skills in Demand) → 186 (ENS)',
    body: 'The employer-sponsored route. From late 2024 the legacy 482 TSS visa has been progressively migrated to the new Skills in Demand (SID) visa with three streams — Specialist Skills (TSMIT $135K+ threshold), Core Skills (CSOL-aligned), and Essential Skills (sectoral). Successful 482/SID holders typically transition to a Subclass 186 Employer Nomination Scheme (ENS) permanent visa after 2–3 years of qualifying employment. Indian healthcare workers, mechanical and civil engineers, ICT specialists and skilled tradespeople are the most-sponsored cohorts.',
    ctaLabel: 'Read the full Employer-Sponsored guide',
    ctaUrl: SC_482_URL,
  },
  {
    icon: Mountain,
    title: 'Subclass 491 / 494 — Skilled Regional (Provisional → 191 PR)',
    body: 'The regional pathway. Subclass 491 is state-nominated or family-sponsored regional; Subclass 494 is employer-sponsored regional. Both add a +15 points regional bonus, both require five-year residence in a designated regional area (everywhere outside Sydney, Melbourne and Brisbane CBDs), and both provide a defined route to a permanent Subclass 191 PR after 3 years of regional work, residence and tax filing. This is consistently the most realistic permanent route for Indian applicants whose 189 score sits below 80.',
    ctaLabel: 'Read the full Regional Migration guide',
    ctaUrl: SC_491_URL,
  },
];

const PROCESS_STEPS = [
  ['01', 'Eligibility Assessment', 'Free 30-minute consultation, age check, CSOL occupation match, English-band baseline, indicative points calculation.'],
  ['02', 'Skills Assessment', 'Lodged with VETASSESS / EA / ACS / ANMAC / TRA / CPA Australia / AHPRA depending on occupation. Allow 8–16 weeks.'],
  ['03', 'IELTS / PTE Preparation', 'Coaching to lift to Competent (6.0) or Proficient (7.0) English — each band adds 0–20 points to the SkillSelect total.'],
  ['04', 'Pathway Strategy & SkillSelect EOI', 'Final recommendation across 189 / 190 / 482 / 491. EOI lodged in SkillSelect with the correct subclass and points claim.'],
  ['05', 'State / Employer Nomination', '190 / 482 / 491 only — state nomination application or employer nomination (SBS + Nomination) submitted with full supporting evidence.'],
  ['06', 'Invitation to Apply (ITA)', 'Federal SkillSelect (189) or state-issued (190 / 491) or employer-driven (482 / 186). Triggers the 60-day visa application window.'],
  ['07', 'Visa Lodgement, Health & Character', 'ImmiAccount lodgement with full documents. Medicals at Bupa Medical Visa Services (India), AFP character check, Indian PCC.'],
  ['08', 'Grant & Settlement', 'Visa grant. Pre-departure briefing, landing-airport pickup, 90-day settlement support (TFN, Medicare, banking, housing, schools).'],
];

const ZAFCO_REASONS = [
  ['MEA-Licensed Recruitment Agency', `RC: ${MEA_REG}. Verifiable on the Ministry of External Affairs\u2019 eMigrate portal.`],
  ['15+ Years in International Mobility', 'Operating Australian files since 2009; through SkillSelect\u2019s 2012 launch, the 2018 reforms, and the 2024–25 CSOL / SID transition.'],
  ['10,000+ Verified Placements', 'Across Australia, Canada, USA, UK, Germany, Saudi Arabia, the UAE and 40+ other destinations.'],
  ['98% File-Success Rate', 'On cases we accept after the initial eligibility assessment. We tell you honestly if your file is not currently competitive.'],
  ['Dedicated Australia Desk', 'In-house specialists across all four pathways (189 / 190 / 482 / 491) and every assessing authority. No outsourcing.'],
  ['SkillSelect & Points-Test Optimisation', 'Sequenced English, PYP, partner-skills and state-nomination claims to maximise the SkillSelect score before lodgement.'],
  ['State Nomination Expertise', 'Active programme knowledge for NSW, VIC, QLD, SA, WA, TAS, ACT, NT. We know which state is inviting your occupation this quarter.'],
  ['Skills Assessment Support', 'Pre-lodgement document review, employment evidence drafting, work-references restructured to assessing-body requirements.'],
  ['Post-Landing Settlement Assistance', '90-day support after landing: TFN, Medicare, banking, housing referrals, school enrolment.'],
  ['Verified Reviews', '4.7★+ on Google and Facebook from past applicants. Real names, real timelines, verifiable on the platforms themselves.'],
];

const STATE_CARDS = [
  { icon: Building2, name: 'New South Wales (NSW)', desc: 'The largest state nomination programme. Strongest demand for ICT, healthcare and engineering. NSW Stream 1 (skilled occupations) and Stream 2 (post-study visa holders) both active. CSOL-aligned occupation list. Sydney is not a regional area; choose 190 for Sydney, 491 for the rest of NSW.', path: '/skilled/australia/new-south-wales-occupation-list-for-subclass-190' },
  { icon: Cpu, name: 'Victoria (VIC)', desc: 'Specialist demand for advanced manufacturing, ICT (cybersecurity, data, software), MedTech, life sciences and healthcare. Target Sector Lists refreshed quarterly. Melbourne is a 491 regional area for postcodes outside the CBD.', path: '/skilled/australia/victoria-state-sponsorship-skilled-occupation-list' },
  { icon: Stethoscope, name: 'Queensland (QLD)', desc: 'Strong for civil and mechanical engineering, healthcare, and education. BSMQ (Business and Skilled Migration Queensland) runs the programme. Brisbane is not a 491 regional area; the Gold Coast and most of regional QLD are.', path: '/skilled/australia/queensland-skilled-occupation-list' },
  { icon: Wine, name: 'South Australia (SA)', desc: 'Australia\u2019s most accessible state for healthcare and aged-care professionals. SA\u2019s Skilled & Business Migration programme runs a generous 491 stream for international graduates and offshore CSOL candidates with state ties.', path: '/skilled/australia/south-australia-state-nomination-skilled-occupation-list' },
  { icon: Wrench, name: 'Western Australia (WA)', desc: 'Dominated by mining, construction, healthcare and skilled trades. WA\u2019s Skilled Migration Western Australia (SMWA) General and Graduate streams remain active in 2026; Schedule 1 and 2 lists reviewed twice a year.', path: '/skilled/australia/western-australian-skilled-migration-occupation-list' },
  { icon: Mountain, name: 'Tasmania (TAS)', desc: 'Smaller intake but one of the easiest state-nomination tracks for low-population-density occupations and post-study international graduates. Tasmanian Skilled Occupation List (TSOL) updated annually; entire state is a designated regional area.', path: '/skilled/australia/tasmanian-skilled-occupation-list' },
];

const UPDATES_2026 = [
  { icon: FileCheck, label: 'Core Skills Occupation List (CSOL)', body: 'Single occupation list since Dec 2024, replacing legacy MLTSSL / STSOL / ROL. Eligibility for 189 / 190 / 491 is now CSOL-aligned.' },
  { icon: Building2, label: 'Skills in Demand (SID) visa', body: 'Three streams: Specialist Skills (high-salary), Core Skills (CSOL-based), Essential Skills (sectoral / regional). Progressively replacing the 482 TSS visa.' },
  { icon: Sparkles, label: '2026 Migration Programme: ~185,000', body: 'Of which ~132,200 places are reserved for the Skill stream.' },
  { icon: GraduationCap, label: '189 cut-offs 75–95 in 2026', body: 'Statutory floor is 65 but invited cut-offs vary by occupation. Healthcare invited lower, accounting / ICT higher.' },
];

const FAQS = [
  { q: 'What is the easiest Australian visa to get from India in 2026?',
    a: 'For most Indian applicants the most realistic permanent route in 2026 is the Subclass 491 Skilled Regional (Provisional) visa with state nomination. The +15 regional points bonus is the single biggest score lever in the Australian system, the 491 leads to a permanent Subclass 191 PR after 3 years of regional residence, and the points cut-offs in regional streams are typically 10–20 points lower than 189 federal rounds. The Subclass 189 is theoretically open to all CSOL occupations but invited cut-offs run 75–95 in 2026 — outside reach for many candidates.' },
  { q: 'What is the minimum points score for Australian PR?',
    a: 'The statutory minimum is 65 points across 189 / 190 / 491. The invited cut-offs are higher and vary by occupation and pathway. In 2026, federal 189 invitations have run 75–95 depending on occupation (healthcare lower, accounting / ICT business analyst higher). 190 invitations typically run 65–85 with the +5 state-nomination bonus included. 491 regional invitations typically run 65–80 with the +15 regional bonus included.' },
  { q: 'What is SkillSelect and how does the EOI work?',
    a: 'SkillSelect is the Australian Department of Home Affairs\u2019 centralised candidate-pool platform. Applicants lodge a free Expression of Interest (EOI) specifying their nominated occupation (from the CSOL), claimed points, English score, work experience and target visa subclass(es). Invitations are issued in monthly federal rounds for 189, and on a rolling basis by states for 190 / 491. An EOI is valid for two years and can be edited any time.' },
  { q: 'What is the Core Skills Occupation List (CSOL)?',
    a: 'The CSOL is the single occupation list introduced in December 2024 by the Department of Home Affairs and Jobs and Skills Australia. It replaced the legacy MLTSSL, STSOL and ROL lists and is now the eligibility source for 189, 190, 491 and the Core Skills stream of the Skills in Demand (SID) visa. The list is reviewed annually based on labour-market data and currently runs to ~456 occupations.' },
  { q: 'Do I need a job offer to apply for Australian PR?',
    a: 'No — neither Subclass 189 nor Subclass 190 requires a job offer. The state-nomination (190) and regional pathways (491) require state government nomination, but that is not a job offer — it is a government endorsement. A job offer is only mandatory for the employer-sponsored routes (Subclass 482 / 186 / 494).' },
  { q: 'What is the maximum age for Australian skilled migration?',
    a: 'Under 45 at the time of invitation for Subclass 189, 190 and 491. Age points are tiered: 30 points for 25–32 years, 25 points for 33–39, 15 points for 40–44 — zero points from 45 onwards (and the candidate is no longer eligible). The 482 / 186 / 494 employer-sponsored stream has the same under-45 rule with limited age exemptions for high-salary or research roles.' },
  { q: 'What is a skills assessment and which authority do I use?',
    a: 'A skills assessment is a mandatory verification by an Australian assessing authority that your overseas qualifications and experience match the Australian standard for your nominated occupation. The correct assessing body is occupation-dependent: VETASSESS for most professional, hospitality and trade occupations; Engineers Australia (EA) for engineering; ACS for ICT; ANMAC for nursing and midwifery; TRA for technical trades; CPA Australia / CA ANZ / IPA for accounting; AHPRA for regulated health professions. Allow 8–16 weeks for a positive assessment to issue.' },
  { q: 'How do state nominations actually work?',
    a: 'Each Australian state and territory publishes its own in-demand occupation list each financial year. To be state-nominated you must (i) have a CSOL occupation that also appears on the nominating state\u2019s in-demand list, (ii) lodge a SkillSelect EOI tagged to that state, (iii) submit a state-nomination application via the state\u2019s portal with evidence of your qualifications, experience, English and (often) a genuine commitment to live and work in the state. Nomination decisions take 4–14 weeks depending on the state and the volume of applications in that round.' },
  { q: 'What is "regional Australia" for migration purposes?',
    a: 'Regional Australia for Subclass 491 / 494 / 191 purposes is defined as everywhere in Australia outside Sydney, Melbourne and Brisbane CBDs and inner-ring suburbs. The entire states of South Australia, Western Australia, Tasmania and the Northern Territory are regional. The ACT and most of regional NSW, VIC and QLD are regional. The Department publishes the full postcode list. Most Indian regional placements go to Adelaide, Perth, Hobart, Darwin, Geelong, Newcastle, Wollongong and the Gold Coast.' },
  { q: 'How is the Skills in Demand (SID) visa different from the old 482 TSS?',
    a: 'The Skills in Demand (SID) visa, progressively replacing the 482 TSS from late 2024, runs three streams: Specialist Skills (TSMIT AUD 135,000+ salary threshold, ~7-day priority processing), Core Skills (CSOL-aligned, mid-tier sponsorship), and Essential Skills (sector-specific — care economy, agriculture). Key changes vs. old 482: a clearer permanent residency pathway, mobility between sponsors (180-day window to find a new sponsor without losing the visa), and labour-market testing exemptions for some categories.' },
  { q: 'Can my family come with me on an Australian skilled visa?',
    a: 'Yes — every Australian skilled-migration visa (189, 190, 491, 494, 482, 186) is family-inclusive. Your spouse or de facto partner and all dependent children under 18 are included on the same application. Accompanying spouses receive full unrestricted work rights from the day the visa is granted. Children receive access to subsidised public schooling. The whole family is covered under Medicare from the day they land (for permanent visas) or via Reciprocal Health Care arrangements (for some provisional visas).' },
  { q: 'How much does Australian skilled migration cost for an Indian applicant in 2026?',
    a: 'Department of Home Affairs visa application charges (2026): AUD 4,765 per primary applicant for Subclass 189 / 190 / 491. Additional applicant charges of AUD 2,385 (spouse) and AUD 1,195 (each child). On top of that budget for skills assessment (AUD 1,150–1,300 depending on body), IELTS / PTE (~INR 17,000 per attempt), medicals (~INR 7,500 per adult), police clearances (Indian PCC ~INR 500, Australian Federal Police ~AUD 56) and any state-nomination fee (AUD 200–500). Zafco\u2019s professional fees are quoted on a milestone basis after the eligibility assessment.' },
];

const CATEGORY_DRAWS = [
  { icon: Stethoscope, name: 'Healthcare', body: 'Nurses, allied health, GPs, aged-care.' },
  { icon: Cpu, name: 'ICT & Engineering', body: 'Software, data, cybersecurity, civil, mechanical, electrical.' },
  { icon: Wrench, name: 'Skilled Trades', body: 'Welders, electricians, plumbers, HVAC, heavy-equipment.' },
  { icon: Truck, name: 'Construction', body: 'Site supervisors, builders, project managers.' },
  { icon: Sprout, name: 'Agriculture', body: 'Agricultural managers, meat processors, horticulturists.' },
  { icon: GraduationCap, name: 'Education', body: 'Secondary teachers, vocational educators, early childhood.' },
];

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid={`australia-hub-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-5 flex items-start justify-between gap-4"
        aria-expanded={open}
      >
        <span className="font-semibold text-zafco-blue">{q}</span>
        <ChevronDown className={`w-5 h-5 text-amber-600 mt-1 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && <p className="text-gray-700 leading-relaxed px-5 pb-5 -mt-1">{a}</p>}
    </div>
  );
};

const AustraliaHubPage = () => {
  usePageMeta(
    'Australia PR Visa & Skilled Migration 2026 from India | ZAFCO',
    'MEA-licensed Australia immigration agency. Compare Subclass 189, 190, 482, 491 — SkillSelect EOI, skills assessment, state nomination. 10,000+ placements.',
    {
      canonical: '/countries/australia',
      image: '/social-media/og-australia-immigration.jpg',
      imageAlt: 'Australia PR Visa, Skilled Migration & State Nomination pathways for Indian professionals — ZAFCO MEA-approved',
    },
  );

  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Countries', item: 'https://zafco.in/countries/australia' },
        { '@type': 'ListItem', position: 3, name: 'Australia', item: 'https://zafco.in/countries/australia' },
      ],
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Australia Skilled Migration Consultancy — 189, 190, 482, 491',
      description: 'MEA-licensed Australian skilled-migration consulting for Indian professionals — SkillSelect EOI strategy, points-test optimisation, state nomination, employer sponsorship and regional pathways.',
      serviceType: 'Australia Immigration Consultancy',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'Australia' },
        { '@type': 'Country', name: 'India' },
      ],
      audience: { '@type': 'Audience', audienceType: 'Indian skilled professionals seeking Australian permanent residency' },
      url: 'https://zafco.in/countries/australia',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-australia-hub', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(faq, 'faq'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  return (
    <div data-testid="australia-hub-page">
      <PageHeader
        title="Australia PR Visa & Skilled Migration for Indian Professionals — 2026 Pathways"
        subtitle="MEA-licensed Australian skilled-migration partner with 10,000+ verified placements across 50+ countries. Compare the four state-recognised pathways, check your eligibility and start your SkillSelect Expression of Interest the right way."
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'Australia', path: '/countries/australia' },
        ]}
        keywordsTier={['MEA Approved', 'SkillSelect', 'State Nomination']}
      />

      {/* Trust strip */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved Recruitment Agency</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Numeric trust stats */}
      <section className="py-10 bg-white border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              ['15+', 'Years Experience'],
              ['10,000+', 'Verified Placements'],
              ['98%', 'Success Rate'],
              ['50+', 'Countries Served'],
            ].map(([n, l]) => (
              <div key={l} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                <div className="text-2xl md:text-3xl font-bold text-zafco-blue">{n}</div>
                <div className="text-xs md:text-sm text-gray-600 mt-1">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="australia-hub-cta-compare">
              <a href="#pathways">
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Compare Pathways
              </a>
            </Button>
            <Button asChild variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="australia-hub-cta-counsellor">
              <Link to="/contact?source=australia-hub-hero">
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" /> Speak to an Australia Counsellor
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white" aria-labelledby="australia-overview">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Australia Corridor · 2026</span>
          <h2 id="australia-overview" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Why Australia remains the most structurally accessible PR for Indians in 2026
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Australia remains one of the most structurally accessible immigration destinations for Indian skilled professionals in 2026. The Department of Home Affairs has set a <strong>2026 Migration Programme of approximately 185,000 places</strong>, with <strong>132,200 reserved for the Skill stream</strong> — the bucket Indian engineers, healthcare workers, IT specialists, accountants, tradespeople and managers compete in. For applicants from India, the system is delivered through Australia&rsquo;s central candidate-pool platform, <strong>SkillSelect</strong>, and the four state-recognised pathways that flow from it: <strong>Subclass 189</strong> (Skilled Independent), <strong>Subclass 190</strong> (Skilled Nominated), <strong>Subclass 482 / 186</strong> (employer-sponsored), and <strong>Subclass 491 / 494</strong> (skilled regional).
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            The headline reasons Indian skilled professionals choose Australia in 2026:
          </p>
          <ul className="grid md:grid-cols-2 gap-3 mb-6">
            {[
              ['Multiple parallel routes', 'SkillSelect, state nomination, employer sponsorship and regional all run concurrently.'],
              ['189 requires no sponsorship', 'A high-points candidate on the CSOL can be invited directly through SkillSelect.'],
              ['State Nomination (190) adds +5 points', 'And prioritises occupations a specific state or territory is short of.'],
              ['Regional pathways (491/494) add +15 points', '5-year provisional → Subclass 191 PR after 3 years regional residence.'],
              ['Skills in Demand (SID) visa', 'Three streams — Specialist, Core, Essential — broaden employer-sponsored eligibility.'],
              ['Family-inclusive', 'Spouses get full work rights, children get subsidised public schooling and Medicare.'],
            ].map(([t, b]) => (
              <li key={t} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-semibold text-zafco-blue">{t}</div>
                  <div className="text-sm text-gray-600 mt-0.5">{b}</div>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-gray-700 leading-relaxed">
            At <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link>, we have managed Australian skilled-migration files since 2009 — through every major reform from the original points test to <Link to={SC_189_URL} className="text-zafco-blue font-semibold hover:text-amber-600">SkillSelect</Link>, the introduction of state nomination, and the 2024–25 CSOL / <Link to={SC_482_URL} className="text-zafco-blue font-semibold hover:text-amber-600">Skills in Demand (SID) visa</Link> transition. Our credentials are matter-of-fact: an active <strong>MEA Recruitment Licence (RC: {MEA_REG})</strong>, <strong>10,000+ verified placements across 50+ countries</strong>, a <strong>98% file-success rate</strong> on cases we accept, and a <strong>dedicated Australia desk</strong> holding in-house expertise on SkillSelect EOI strategy, skills assessments (VETASSESS, EA, ACS, ANMAC) and state-nomination optimisation.
          </p>
        </div>
      </section>

      {/* 2026 Updates Callout */}
      <section className="py-16 bg-white border-t border-slate-200" aria-labelledby="australia-2026-updates">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-amber-500 rounded-2xl p-6 md:p-8">
            <h2 id="australia-2026-updates" className="text-2xl md:text-3xl font-bold text-zafco-blue font-syne mb-2 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-amber-600" aria-hidden="true" /> What changed for Australian skilled migration in 2026
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {UPDATES_2026.map((u) => {
                const Icon = u.icon;
                return (
                  <div key={u.label} className="bg-white rounded-xl p-4 border border-amber-200/60">
                    <div className="flex items-start gap-3">
                      <div className="bg-amber-100 text-amber-700 p-2 rounded-lg flex-shrink-0">
                        <Icon className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-semibold text-zafco-blue text-sm">{u.label}</div>
                        <div className="text-xs text-gray-600 mt-1 leading-relaxed">{u.body}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              {CATEGORY_DRAWS.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.name} className="bg-white rounded-lg p-3 border border-amber-200/60 flex items-start gap-2">
                    <Icon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <div>
                      <div className="text-xs font-semibold text-zafco-blue">{c.name}</div>
                      <div className="text-xs text-gray-600">{c.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Four Pathways Comparison */}
      <section id="pathways" className="py-16 bg-slate-50 border-t border-slate-200" aria-labelledby="australia-pathways">
        <div className="container mx-auto px-6 max-w-7xl">
          <h2 id="australia-pathways" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Four pathways to Australian PR — at a glance
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6 max-w-3xl">
            Four state-recognised pathways lead to an Australian PR or a defined PR endpoint. All four operate concurrently in 2026, all four feed off the same SkillSelect Expression of Interest infrastructure, and all four are open to Indian nationals.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-xs md:text-sm">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-3 py-3 font-semibold w-[14%]">Feature</th>
                  <th className="text-left px-3 py-3 font-semibold">Subclass 189 — Skilled Independent</th>
                  <th className="text-left px-3 py-3 font-semibold">Subclass 190 — Skilled Nominated</th>
                  <th className="text-left px-3 py-3 font-semibold">Subclass 482 / 186 — Employer-Sponsored</th>
                  <th className="text-left px-3 py-3 font-semibold">Subclass 491 / 494 — Skilled Regional</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((r, idx) => (
                  <tr key={r.feature} className={idx % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-3 py-3 font-semibold text-zafco-blue align-top">{r.feature}</td>
                    <td className="px-3 py-3 text-gray-700 align-top">{r.sc189}</td>
                    <td className="px-3 py-3 text-gray-700 align-top">{r.sc190}</td>
                    <td className="px-3 py-3 text-gray-700 align-top">{r.sc482}</td>
                    <td className="px-3 py-3 text-gray-700 align-top">{r.sc491}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 justify-between" data-testid="australia-hub-cta-189">
              <Link to={PR_VISA_URL}>Learn About Australia PR Visa <ArrowUpRight className="w-4 h-4" aria-hidden="true" /></Link>
            </Button>
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 justify-between" data-testid="australia-hub-cta-190">
              <Link to={SC_190_URL}>Explore State Nomination <ArrowUpRight className="w-4 h-4" aria-hidden="true" /></Link>
            </Button>
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 justify-between" data-testid="australia-hub-cta-482">
              <Link to={SC_482_URL}>Check Employer-Sponsored <ArrowUpRight className="w-4 h-4" aria-hidden="true" /></Link>
            </Button>
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 justify-between" data-testid="australia-hub-cta-491">
              <Link to={SC_491_URL}>See Regional Migration <ArrowUpRight className="w-4 h-4" aria-hidden="true" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Pathway Detail Blocks */}
      <section className="py-16 bg-white" aria-labelledby="australia-pathway-details">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="australia-pathway-details" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            The four pathways in detail
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            Each pathway has a distinct selection model, points logic and end-state. Pick the one that matches your profile — or talk to our Australia desk for a tailored recommendation.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {PATHWAY_BLOCKS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-amber-100 text-amber-700 p-2.5 rounded-xl flex-shrink-0">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-zafco-blue text-lg leading-tight">{p.title}</h3>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">{p.body}</p>
                  <Link
                    to={p.ctaUrl}
                    className="text-amber-700 font-semibold hover:underline inline-flex items-center gap-1 text-sm"
                  >
                    {p.ctaLabel} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Decision Guide */}
      <section className="py-16 bg-slate-50" aria-labelledby="australia-decision-guide">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="australia-decision-guide" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Quick decision guide — which pathway fits your profile?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6 max-w-3xl">
            If you are still uncertain which pathway suits your profile, the matrix below reflects how our Australia desk typically routes incoming files. Each CTA opens the consultation form with the lead source tagged for our Australia desk to route the file to the correct specialist within one working day.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm md:text-[15px]">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Your situation</th>
                  <th className="text-left px-4 py-3 font-semibold">Recommended pathway</th>
                  <th className="text-left px-4 py-3 font-semibold">Next step</th>
                </tr>
              </thead>
              <tbody>
                {DECISION_ROWS.map(([situation, pathway, cta, href], idx) => (
                  <tr key={situation} className={idx % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-4 py-3 text-gray-700 align-top">{situation}</td>
                    <td className="px-4 py-3 text-gray-700 align-top font-medium">{pathway}</td>
                    <td className="px-4 py-3 align-top">
                      <Link to={href} className="text-amber-700 font-semibold hover:underline inline-flex items-center gap-1" data-testid={`australia-hub-decision-${idx}`}>
                        {cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6">
            <Link to={POINTS_CALC_URL} className="inline-flex items-center gap-2 text-zafco-blue font-semibold hover:text-amber-600">
              <FileCheck className="w-4 h-4" aria-hidden="true" />
              Calculate my SkillSelect points <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 bg-white" aria-labelledby="australia-process">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="australia-process" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Our eight-stage Australian skilled-migration process
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            We deliver every Australian skilled-migration file through an eight-stage framework. Pathway-specific points and nomination steps happen at stages 3–5 — the rest is identical across 189 / 190 / 482 / 491.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PROCESS_STEPS.map(([n, name, body]) => (
              <div key={n} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-amber-600 text-sm font-bold tracking-wider">{n}</div>
                <div className="font-semibold text-zafco-blue mt-1">{name}</div>
                <div className="text-xs text-gray-600 mt-1 leading-relaxed">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* State & Territory Snapshot */}
      <section className="py-16 bg-slate-50" aria-labelledby="australia-states">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="australia-states" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            State & Territory nomination programmes we operate
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            Six Australian states and two territories operate their own nomination programmes in 2026. The six we route Indian applicants through most often, with their 2026 commercial signal:
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {STATE_CARDS.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.name}
                  to={s.path}
                  className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-amber-400 transition-colors block"
                  data-testid={`australia-hub-state-${s.name.split(' ')[0].toLowerCase()}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-100 text-amber-700 p-2.5 rounded-xl flex-shrink-0">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zafco-blue mb-1.5 flex items-center gap-2">
                        {s.name} <ArrowUpRight className="w-4 h-4 opacity-60" aria-hidden="true" />
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          <p className="text-sm text-gray-600 mt-6 max-w-3xl">
            The <strong>Australian Capital Territory (ACT)</strong> and <strong>Northern Territory (NT)</strong> also operate nomination programmes — ACT for ICT, healthcare, engineering and education; NT for healthcare, construction and hospitality, with one of the lowest state-points thresholds in Australia. Our Australia desk can route either where the candidate profile fits.
          </p>
        </div>
      </section>

      {/* Why Choose Zafco */}
      <section className="py-16 bg-white" aria-labelledby="why-zafco-australia">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="why-zafco-australia" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Why choose ZAFCO for your Australian skilled-migration file
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            The 10 reasons Indian skilled professionals route their Australian file through ZAFCO instead of going direct or through an unlicensed consultant.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {ZAFCO_REASONS.map(([t, b], idx) => (
              <motion.div
                key={t}
                className="flex items-start gap-3 bg-slate-50 p-5 rounded-xl border border-slate-200"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.03 }}
              >
                <div className="bg-zafco-blue/10 text-zafco-blue rounded-lg p-2 flex-shrink-0">
                  <CheckCircle className="w-5 h-5" aria-hidden="true" />
                </div>
                <div>
                  <div className="font-semibold text-zafco-blue">{idx + 1}. {t}</div>
                  <div className="text-sm text-gray-600 mt-1 leading-relaxed">{b}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success story */}
      <section className="py-16 bg-slate-900 text-white" aria-labelledby="australia-success">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 id="australia-success" className="sr-only">Success Story</h2>
          <blockquote className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-8">
            <p className="text-lg text-slate-100 leading-relaxed italic">
              &ldquo;My 189 score sat at 75 — competitive but not invited for nine months. Zafco&rsquo;s Australia desk rebuilt my profile, secured a Subclass 190 nomination from Victoria as a mechanical engineer (CSOL occupation, Target Sector List match), and lifted my IELTS from 6.5 to 7.5 across all four bands to claim the Proficient English +10 points. The combined effect took me from 75 to 95. I received the ITA within five weeks of EOI lodgement and the grant 92 days later.&rdquo;
            </p>
            <footer className="mt-5 text-amber-400 font-semibold">
              — Vikrant Sharma, <span className="text-slate-300 font-normal">Mechanical Engineer, formerly Larsen &amp; Toubro (Mumbai), now Worley Engineering (Melbourne). Deployed via Zafco Australia desk, 11-month total timeline.</span>
            </footer>
          </blockquote>
        </div>
      </section>

      {/* Final CTAs */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-slate-900 text-white" aria-labelledby="australia-final-cta">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="australia-final-cta" className="text-3xl md:text-4xl font-bold font-syne mb-3">
            Take the next step on your Australian skilled-migration journey
          </h2>
          <p className="text-slate-300 mb-8 max-w-3xl">
            Tell us your profile in 60 seconds — our Australia desk responds within one working day with a points pre-score, pathway recommendation and indicative timeline.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/contact?source=australia-hub-final-cta"
              className="bg-amber-500 text-slate-900 rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-amber-400 transition-colors"
              data-testid="australia-hub-final-cta-recommendation"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-700">Free pathway analysis</span>
                <span className="text-lg">Compare 189 vs 190 vs 482 vs 491 — Get My Recommendation</span>
              </span>
              <ArrowUpRight className="w-6 h-6" aria-hidden="true" />
            </Link>
            <a
              href={`tel:${PRIMARY_PHONE.replace(/[^+\d]/g, '')}`}
              className="bg-slate-800/60 border border-slate-700 text-white rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-slate-800 transition-colors"
              data-testid="australia-hub-final-cta-phone"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-400">Speak to our Australia desk</span>
                <span className="text-lg">{PRIMARY_PHONE}</span>
              </span>
              <Phone className="w-6 h-6 text-amber-400" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-16 bg-white" id="contact" aria-labelledby="australia-contact-form">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-8">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Talk to our Australia desk</span>
            <h2 id="australia-contact-form" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
              Start your Australian skilled-migration file
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Share your profile — qualification, work experience, IELTS band, age, target state (if any). Our Australia desk responds within one working day with a points pre-score and pathway recommendation.
            </p>
          </div>
          <ConsultationForm sourceTag="australia-hub" />
          <div className="text-center text-sm text-gray-500 mt-6">
            Prefer email? <a href={`mailto:${PRIMARY_EMAIL}?subject=Australia%20skilled%20migration%20enquiry`} className="text-zafco-blue font-semibold hover:underline">
              <Mail className="w-4 h-4 inline mr-1" aria-hidden="true" />{PRIMARY_EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-slate-50" aria-labelledby="australia-faqs">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 id="australia-faqs" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Frequently asked questions about Australian skilled migration
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl">
            Twelve high-intent questions covering points cut-offs, SkillSelect mechanics, state nomination, regional pathways and costs — the questions our Australia counsellors are asked every day.
          </p>
          <div className="space-y-3">
            {FAQS.map((f, idx) => (
              <FaqItem key={f.q} q={f.q} a={f.a} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      <div className="text-center py-6 text-xs text-gray-500 bg-white border-t border-slate-200">
        Last reviewed: February 2026 · Reviewed by ZAFCO Senior Australia Counsellor
      </div>
    </div>
  );
};

export default AustraliaHubPage;
